// src/ui.js
// Minimal, dependency-free UI primitives for consistent look.
// Usage: import { Panel, Toolbar, Button, DangerButton, Input, Select, Field, H3, H4 } from '../ui'

import React, { useEffect, useState } from 'react';

const base = {
  radius: 10,
  pad: 12,
  border: '1px solid #e5e7eb',
  font: `-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Inter,Helvetica,Arial,sans-serif`,
};

const LIME = '#83d466';

function useIsNarrow(breakpoint = 640) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setNarrow(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);

  return narrow;
}

export function Panel({ title, right, children, style }) {
  return (
    <section
      style={{
        border: base.border,
        borderRadius: base.radius,
        padding: base.pad,
        background: '#ffffff',
        ...style,
      }}
    >
      {title && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <H4 style={{ margin: 0 }}>{title}</H4>
          {right || null}
        </div>
      )}
      {children}
    </section>
  );
}

export function Toolbar({ children, style }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
        border: base.border,
        borderRadius: base.radius,
        padding: 8,
        background: '#fff',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Button(props) {
  return (
    <button
      {...props}
      style={{
        background: LIME,
        color: '#000',
        border: 'none',
        padding: '8px 16px',
        borderRadius: 6,
        cursor: 'pointer',
        fontWeight: 600,
        transition: '0.15s ease',
        opacity: props.disabled ? 0.6 : 1,
        ...props.style,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.filter = 'brightness(0.9)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.filter = 'brightness(1)';
      }}
    />
  );
}

export function GhostButton(props) {
  return (
    <button
      {...props}
      style={{
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid #d0d7de',
        background: '#fff',
        color: '#111',
        fontWeight: 600,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        ...props.style,
      }}
    />
  );
}

export function DangerButton(props) {
  return <Button {...props} style={{ background: '#ef4444', color: 'white', ...props.style }} />;
}

export function Input(props) {
  return (
    <input
      {...props}
      style={{
        width: '100%',
        padding: 8,
        borderRadius: 8,
        border: '1px solid #d0d7de',
        ...props.style,
      }}
    />
  );
}
export function Select(props) {
  return (
    <select
      {...props}
      style={{
        width: '100%',
        padding: 8,
        borderRadius: 8,
        border: '1px solid #d0d7de',
        ...props.style,
      }}
    />
  );
}
export function Textarea(props) {
  return (
    <textarea
      {...props}
      style={{
        width: '100%',
        padding: 8,
        borderRadius: 8,
        border: '1px solid #d0d7de',
        ...props.style,
      }}
    />
  );
}

export function Field({ label, children, twoCol = false }) {
  const isNarrow = useIsNarrow();

  if (twoCol) {
    // On mobile: stack label + input; on desktop: 160px / 1fr grid
    const wrapperStyle = isNarrow
      ? {
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          alignItems: 'stretch',
          marginBottom: 8,
        }
      : {
          display: 'grid',
          gridTemplateColumns: '160px 1fr',
          gap: 8,
          alignItems: 'center',
          marginBottom: 8,
        };

    const labelStyle = {
      color: '#444',
      fontSize: 13,
    };

    return (
      <div style={wrapperStyle}>
        <label style={labelStyle}>{label}</label>
        <div>{children}</div>
      </div>
    );
  }

  return (
    <label style={{ display: 'block', color: '#444' }}>
      {label}
      <div style={{ marginTop: 4 }}>{children}</div>
    </label>
  );
}

export function H3({ children, style }) {
  return (
    <h3
      style={{
        margin: '4px 0 8px',
        fontFamily: base.font,
        ...style,
      }}
    >
      {children}
    </h3>
  );
}
export function H4({ children, style }) {
  return (
    <h4
      style={{
        margin: '4px 0 8px',
        fontFamily: base.font,
        ...style,
      }}
    >
      {children}
    </h4>
  );
}
